import React, { useEffect, useState } from 'react';
import {
  ACCENT,
  ACCENT_LIGHT,
  ACCENT_BORDER,
  TEXT_DARK,
  TEXT_MID,
  TEXT_LIGHT,
  FONT,
  ERROR_RED,
} from '../lib/tokens';
import { Ic } from '../components/Icons';
import { Card, GhostCard, ScreenWrap, SubmitBtn, Field, SectionLabel } from '../components/ui/Primitives';
import { supabase, hasSupabase } from '../lib/supabase';
import { adminCall, setAdminToken, clearAdminToken, getAdminToken } from '../lib/adminApi';
import { useBreakpoint } from '../lib/useBreakpoint';

const ADMIN_NAV = [
  { id: 'overview', label: 'Event Status' },
  { id: 'attendees', label: 'Attendees' },
  { id: 'summaries', label: 'Session Summaries' },
  { id: 'parking-lot', label: 'Parking Lot' },
  { id: 'uploads', label: 'Uploads & Links' },
  { id: 'resources', label: 'Resource Library' },
  { id: 'commitments', label: 'Commitments Board' },
];

export default function AdminScreen({ isPostEvent, setIsPostEvent }) {
  const [authed, setAuthed] = useState(() => !!getAdminToken());
  const [view, setView] = useState(null); // null = main menu (mobile) or 'overview' (desktop)
  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop';

  if (!authed) {
    return <AdminLogin onAuthed={() => setAuthed(true)} />;
  }

  const onSignOut = () => {
    clearAdminToken();
    setAuthed(false);
  };

  // ── Desktop: sidebar + content pane ────────────────────────────────────
  if (isDesktop) {
    const active = view || 'overview';
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '240px 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {ADMIN_NAV.map((item) => {
            const isActive = active === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setView(item.id === 'overview' ? null : item.id)}
                style={{
                  textAlign: 'left',
                  padding: '10px 14px',
                  border: 'none',
                  background: isActive ? 'rgba(27,108,181,0.18)' : 'transparent',
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                  fontFamily: FONT,
                  fontSize: 13.5,
                  fontWeight: isActive ? 600 : 500,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                {item.label}
              </button>
            );
          })}
          <button
            onClick={onSignOut}
            style={{
              marginTop: 10,
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.35)',
              fontSize: 12,
              padding: '6px 14px',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Sign out
          </button>
        </nav>

        <div>
          {active === 'overview' && (
            <AdminMain
              isPostEvent={isPostEvent}
              setIsPostEvent={setIsPostEvent}
              setView={setView}
              onSignOut={onSignOut}
              desktop
            />
          )}
          {active === 'attendees' && <AttendeesAdmin onBack={() => setView(null)} desktop />}
          {active === 'parking-lot' && <ParkingLotAdmin onBack={() => setView(null)} desktop />}
          {active === 'uploads' && <UploadsAdmin onBack={() => setView(null)} desktop />}
          {active === 'summaries' && <SummariesAdmin onBack={() => setView(null)} desktop />}
          {active === 'resources' && <ResourcesAdmin onBack={() => setView(null)} desktop />}
          {active === 'commitments' && <CommitmentsAdmin onBack={() => setView(null)} desktop />}
        </div>
      </div>
    );
  }

  // ── Mobile / tablet: full-screen swap (original behaviour) ─────────────
  if (view === 'attendees') return <AttendeesAdmin onBack={() => setView(null)} />;
  if (view === 'parking-lot') return <ParkingLotAdmin onBack={() => setView(null)} />;
  if (view === 'uploads') return <UploadsAdmin onBack={() => setView(null)} />;
  if (view === 'summaries') return <SummariesAdmin onBack={() => setView(null)} />;
  if (view === 'resources') return <ResourcesAdmin onBack={() => setView(null)} />;
  if (view === 'commitments') return <CommitmentsAdmin onBack={() => setView(null)} />;

  return (
    <AdminMain
      isPostEvent={isPostEvent}
      setIsPostEvent={setIsPostEvent}
      setView={setView}
      onSignOut={onSignOut}
    />
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Login
// ────────────────────────────────────────────────────────────────────────────

function AdminLogin({ onAuthed }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const tryAuth = async () => {
    if (!pw) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/admin-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) {
        const { token } = await res.json();
        setAdminToken(token);
        onAuthed();
      } else if (res.status === 401) {
        setError('Incorrect password.');
        setPw('');
      } else {
        setError('Server error. Make sure netlify dev is running.');
      }
    } catch {
      setError('Could not reach the admin server.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScreenWrap>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: '30px 20px',
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 14,
            background: ACCENT_LIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ic.Shield size={26} color={ACCENT} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', textAlign: 'center' }}>
          Admin Access
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.45)',
            textAlign: 'center',
            marginBottom: 6,
          }}
        >
          Enter the admin password to continue.
        </div>
        <div style={{ width: '100%', maxWidth: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tryAuth()}
            placeholder="Password"
            autoFocus
            style={{
              width: '100%',
              padding: '11px 14px',
              border: error ? `1.5px solid ${ERROR_RED}` : '1.5px solid rgba(255,255,255,0.15)',
              borderRadius: 9,
              background: 'rgba(255,255,255,0.07)',
              color: '#fff',
              fontSize: 14,
              fontFamily: FONT,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {error && (
            <div style={{ fontSize: 12.5, color: ERROR_RED, textAlign: 'center' }}>{error}</div>
          )}
          <SubmitBtn onClick={tryAuth} disabled={busy}>
            {busy ? 'Checking…' : 'Unlock'}
          </SubmitBtn>
        </div>
      </div>
    </ScreenWrap>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main admin menu
// ────────────────────────────────────────────────────────────────────────────

function AdminMain({ isPostEvent, setIsPostEvent, setView, onSignOut, desktop }) {
  const [toggling, setToggling] = useState(false);
  const [testSession, setTestSession] = useState(null);
  const [removingTest, setRemovingTest] = useState(false);

  useEffect(() => {
    if (!hasSupabase) return;
    supabase
      .from('sessions')
      .select('id,title')
      .eq('id', 'test-live')
      .maybeSingle()
      .then(({ data }) => setTestSession(data));
  }, []);

  const removeTest = async () => {
    if (!confirm('Remove the test session? You can re-add one later if needed.')) return;
    setRemovingTest(true);
    try {
      await adminCall('sessions.delete', { id: 'test-live' });
      setTestSession(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setRemovingTest(false);
    }
  };

  const onToggleMode = async () => {
    setToggling(true);
    try {
      const next = !isPostEvent;
      await adminCall('event-mode.set', { value: next ? 'post_event' : 'live' });
      setIsPostEvent(next);
    } catch (e) {
      alert('Could not flip event mode: ' + e.message);
    } finally {
      setToggling(false);
    }
  };

  const actions = [
    {
      label: 'Event Mode',
      desc: isPostEvent ? 'Currently: Post-Event' : 'Currently: Live',
      kind: 'toggle',
      action: onToggleMode,
      value: isPostEvent,
      busy: toggling,
    },
    {
      label: 'Manage Attendees',
      desc: 'Add, edit, or remove attendees and toggle contact sharing',
      kind: 'btn',
      action: () => setView('attendees'),
    },
    {
      label: 'Publish Session Summaries',
      desc: 'Write or edit per-session recaps shown in Post-Event view',
      kind: 'btn',
      action: () => setView('summaries'),
    },
    {
      label: 'Manage Parking Lot',
      desc: 'Pin, hide, or delete submitted ideas',
      kind: 'btn',
      action: () => setView('parking-lot'),
    },
    {
      label: 'Review Document Uploads',
      desc: 'Approve, reject, or delete attendee uploads and shared links',
      kind: 'btn',
      action: () => setView('uploads'),
    },
    {
      label: 'Edit Resource Library',
      desc: 'Add or remove links shown on the Resources tab',
      kind: 'btn',
      action: () => setView('resources'),
    },
    {
      label: 'View All Commitments',
      desc: 'Full board of ED commitments',
      kind: 'btn',
      action: () => setView('commitments'),
    },
  ];

  // Filter the action list on desktop — the sidebar replaces the navigation
  // buttons, so the content pane only needs the toggle + status pieces.
  const visibleActions = desktop ? actions.filter((a) => a.kind === 'toggle') : actions;

  // Reference stable module-scope component (DesktopColumn defined below);
  // declaring an arrow function here would change component identity every
  // render and reset scroll on the polling event-mode toggle.
  const Wrap = desktop ? AdminDesktopWrap : ScreenWrap;

  return (
    <Wrap>
      {!desktop && (
        <GhostCard
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 14px',
          }}
        >
          <Ic.Shield size={18} color={ACCENT} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>
              Admin Mode Active
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
              Changes take effect immediately
            </div>
          </div>
          <button
            onClick={onSignOut}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 7,
              padding: '5px 10px',
              color: 'rgba(255,255,255,0.65)',
              fontSize: 11.5,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Sign out
          </button>
        </GhostCard>
      )}

      {testSession && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: 12,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#92400e' }}>
              Pre-Event Countdown Active
            </div>
            <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 2, lineHeight: 1.45 }}>
              "{testSession.title}" is showing on the Home screen and counting down to Session 0. Remove it whenever you no longer want the pre-event banner.
            </div>
          </div>
          <button
            onClick={removeTest}
            disabled={removingTest}
            style={{
              background: '#b45309',
              border: 'none',
              borderRadius: 7,
              padding: '7px 12px',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: removingTest ? 'wait' : 'pointer',
              fontFamily: FONT,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {removingTest ? 'Removing…' : 'Remove'}
          </button>
        </div>
      )}

      {visibleActions.map((a, i) => (
        <Card
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '13px 15px',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: TEXT_DARK }}>{a.label}</div>
            <div style={{ fontSize: 12, color: TEXT_LIGHT, marginTop: 2 }}>{a.desc}</div>
          </div>
          {a.kind === 'toggle' ? (
            <button
              onClick={a.action}
              disabled={a.busy}
              style={{
                background: 'none',
                border: 'none',
                cursor: a.busy ? 'wait' : 'pointer',
                padding: 2,
                opacity: a.busy ? 0.6 : 1,
              }}
            >
              <Ic.Toggle size={22} on={a.value} color={ACCENT} />
            </button>
          ) : (
            <button
              onClick={a.action}
              style={{
                background: ACCENT_LIGHT,
                border: `1px solid ${ACCENT_BORDER}`,
                borderRadius: 8,
                padding: '7px 13px',
                color: ACCENT,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: FONT,
                whiteSpace: 'nowrap',
              }}
            >
              Open
            </button>
          )}
        </Card>
      ))}
    </Wrap>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sub-view: header with back button
// ────────────────────────────────────────────────────────────────────────────

function SubHeader({ title, onBack }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 4,
      }}
    >
      <button
        onClick={onBack}
        style={{
          background: 'rgba(255,255,255,0.07)',
          border: 'none',
          borderRadius: 8,
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <Ic.ArrowLeft size={14} color="#fff" />
      </button>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{title}</div>
    </div>
  );
}

// Wraps each admin sub-view. On mobile it provides the original ScreenWrap +
// back-button header. On desktop the parent (AdminScreen) already renders the
// sidebar, so we drop the chrome and just provide a flex-column container.
function AdminDesktopWrap({ children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>;
}

function AdminPaneShell({ title, onBack, desktop, children }) {
  if (desktop) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>;
  }
  return (
    <ScreenWrap>
      <SubHeader title={title} onBack={onBack} />
      {children}
    </ScreenWrap>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Parking Lot admin
// ────────────────────────────────────────────────────────────────────────────

function ParkingLotAdmin({ onBack, desktop }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!hasSupabase) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('parking_lot')
      .select('*')
      .order('created_at', { ascending: false });
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (row, field) => {
    try {
      const updated = await adminCall('parking-lot.update', {
        id: row.id,
        fields: { [field]: !row[field] },
      });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated.row : r)));
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (row) => {
    if (!confirm(`Delete this idea from ${row.name}?`)) return;
    try {
      await adminCall('parking-lot.delete', { id: row.id });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <AdminPaneShell title="Manage Parking Lot" onBack={onBack} desktop={desktop}>
      {loading && <SectionLabel>LOADING…</SectionLabel>}
      {!loading && rows.length === 0 && <EmptyState text="No ideas submitted yet." />}
      {rows.map((row) => (
        <Card key={row.id} style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>{row.name}</div>
              <div style={{ fontSize: 10.5, color: TEXT_LIGHT }}>{formatTime(row.created_at)} · {row.session_tag || 'Live'}</div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <StatusPill on={row.pinned} onLabel="Pinned" offLabel="Pin" onClick={() => toggle(row, 'pinned')} />
              <StatusPill on={row.hidden} onLabel="Hidden" offLabel="Hide" onClick={() => toggle(row, 'hidden')} variant="warning" />
            </div>
          </div>
          <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.5, margin: '4px 0 8px' }}>{row.idea}</p>
          <button
            onClick={() => remove(row)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: ERROR_RED,
              fontSize: 11.5,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Delete
          </button>
        </Card>
      ))}
    </AdminPaneShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Uploads admin
// ────────────────────────────────────────────────────────────────────────────

function UploadsAdmin({ onBack, desktop }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Read via the admin function — anon RLS on `uploads` hides pending rows.
      const { rows: data } = await adminCall('uploads.list');
      setRows(data || []);
    } catch (e) {
      setError(e.message || 'Could not load uploads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setApproved = async (row, value) => {
    try {
      const updated = await adminCall('uploads.update', {
        id: row.id,
        fields: { approved: value },
      });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated.row : r)));
      // Server returns a `warning` field when approval succeeded but the
      // RAG ingestion silently failed (file uploads only — link failures
      // come back as 422 / thrown errors that the catch below surfaces).
      if (updated.warning) {
        alert(
          `Approved, but the AI couldn't index this upload:\n\n${updated.warning}\n\nYou can retry via Reindex in admin.`,
        );
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (row) => {
    const what = row.link_url ? 'link' : 'file';
    const extra = row.link_url ? '' : ' This removes the file from storage too.';
    if (!confirm(`Delete this ${what} ("${row.file_name}")?${extra}`)) return;
    try {
      await adminCall('uploads.delete', { id: row.id });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e.message);
    }
  };

  const open = async (row) => {
    // Link rows: open the destination URL in a new tab — no signed URL needed.
    if (row.link_url) {
      window.open(row.link_url, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      // Pass `id` so the server can look up the original filename and put it
      // in the signed URL's Content-Disposition header. That's the only way
      // to force a download from Supabase Storage cross-origin — the local
      // <a download> attribute is ignored on cross-origin URLs.
      const { url } = await adminCall('uploads.sign', { id: row.id });
      if (!url) throw new Error('No URL returned');
      const a = document.createElement('a');
      a.href = url;
      a.download = row.file_name || '';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  const pending = rows.filter((r) => !r.approved);
  const approved = rows.filter((r) => r.approved);

  return (
    <AdminPaneShell title="Review Uploads & Links" onBack={onBack} desktop={desktop}>
      {loading && <SectionLabel>LOADING…</SectionLabel>}
      {error && <EmptyState text={error} />}
      {!loading && !error && rows.length === 0 && (
        <EmptyState text="No uploads or links yet." />
      )}

      {pending.length > 0 && <SectionLabel>PENDING · {pending.length}</SectionLabel>}
      {pending.map((row) => (
        <UploadCard key={row.id} row={row} onOpen={() => open(row)}>
          <button
            onClick={() => setApproved(row, true)}
            style={pillBtn('success')}
          >
            Approve
          </button>
          <button onClick={() => remove(row)} style={pillBtn('danger')}>
            Delete
          </button>
        </UploadCard>
      ))}

      {approved.length > 0 && <SectionLabel>APPROVED · {approved.length}</SectionLabel>}
      {approved.map((row) => (
        <UploadCard key={row.id} row={row} onOpen={() => open(row)}>
          <button
            onClick={() => setApproved(row, false)}
            style={pillBtn('muted')}
          >
            Unapprove
          </button>
          <button onClick={() => remove(row)} style={pillBtn('danger')}>
            Delete
          </button>
        </UploadCard>
      ))}
    </AdminPaneShell>
  );
}

function UploadCard({ row, onOpen, children }) {
  const isLink = !!row.link_url;
  return (
    <Card style={{ padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: ACCENT_LIGHT,
            border: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isLink ? (
            <Ic.Link size={13} color={ACCENT} />
          ) : (
            <Ic.Upload size={14} color={ACCENT} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: TEXT_DARK,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.file_name}
          </div>
          {isLink && (
            <div
              style={{
                fontSize: 10.5,
                color: ACCENT,
                marginTop: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={row.link_url}
            >
              {row.link_url}
            </div>
          )}
          <div style={{ fontSize: 11, color: TEXT_LIGHT, marginTop: 1 }}>
            {isLink ? 'Link · ' : ''}
            {row.uploaded_by || 'Unknown'} · {formatTime(row.created_at)}
          </div>
        </div>
        <button onClick={onOpen} style={pillBtn('neutral')}>
          {isLink ? 'Open' : 'Download'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{children}</div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Session summaries admin
// ────────────────────────────────────────────────────────────────────────────

function SummariesAdmin({ onBack, desktop }) {
  const [sessions, setSessions] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [editing, setEditing] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!hasSupabase) return;
    const [{ data: sess }, { data: sum }] = await Promise.all([
      supabase.from('sessions').select('*').order('sort_order'),
      supabase.from('session_summaries').select('*'),
    ]);
    setSessions(sess || []);
    const byId = {};
    for (const s of sum || []) byId[s.id] = s;
    setSummaries(byId);
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (s) => {
    setEditing(s.id);
    const existing = summaries[s.id];
    setDrafts((d) => ({ ...d, [s.id]: existing?.summary || '' }));
  };

  const save = async (s) => {
    setSaving(true);
    try {
      const updated = await adminCall('summaries.upsert', {
        row: {
          id: s.id,
          session_num: s.num_label,
          title: s.title,
          summary: drafts[s.id] || '',
        },
      });
      setSummaries((prev) => ({ ...prev, [s.id]: updated.row }));
      setEditing(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!confirm(`Remove the published summary for ${s.num_label}?`)) return;
    try {
      await adminCall('summaries.delete', { id: s.id });
      setSummaries((prev) => {
        const { [s.id]: _, ...rest } = prev;
        return rest;
      });
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <AdminPaneShell title="Publish Session Summaries" onBack={onBack} desktop={desktop}>
      <div
        style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.45)',
          padding: '0 2px 8px',
          lineHeight: 1.5,
        }}
      >
        Published summaries appear on the Post-Event tab once event mode is flipped.
      </div>
      {sessions.map((s) => {
        const summary = summaries[s.id];
        const isEditing = editing === s.id;
        return (
          <Card key={s.id} style={{ padding: '13px 14px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 4,
                gap: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: ACCENT,
                    letterSpacing: '0.07em',
                  }}
                >
                  {s.num_label?.toUpperCase()}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: TEXT_DARK }}>{s.title}</div>
              </div>
              {summary && !isEditing && (
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: '#16a34a',
                    background: '#dcfce7',
                    border: '1px solid #86efac',
                    borderRadius: 20,
                    padding: '2px 8px',
                  }}
                >
                  PUBLISHED
                </span>
              )}
            </div>
            {!isEditing ? (
              <>
                {summary && (
                  <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.55, margin: '6px 0 8px' }}>
                    {summary.summary}
                  </p>
                )}
                {!summary && (
                  <div
                    style={{
                      fontSize: 12,
                      color: TEXT_LIGHT,
                      fontStyle: 'italic',
                      margin: '6px 0 8px',
                    }}
                  >
                    No summary yet.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => startEdit(s)} style={pillBtn('neutral')}>
                    {summary ? 'Edit' : 'Write summary'}
                  </button>
                  {summary && (
                    <button onClick={() => remove(s)} style={pillBtn('danger')}>
                      Remove
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <textarea
                  value={drafts[s.id] || ''}
                  onChange={(e) => setDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                  rows={6}
                  placeholder="Write a recap of what this session covered…"
                  style={{
                    width: '100%',
                    margin: '8px 0',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: 8,
                    padding: '9px 11px',
                    fontSize: 13,
                    color: TEXT_DARK,
                    resize: 'vertical',
                    fontFamily: FONT,
                    outline: 'none',
                    background: '#fff',
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => save(s)} disabled={saving} style={pillBtn('success')}>
                    {saving ? 'Saving…' : 'Publish'}
                  </button>
                  <button onClick={() => setEditing(null)} style={pillBtn('muted')}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </Card>
        );
      })}
    </AdminPaneShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Resources admin
// ────────────────────────────────────────────────────────────────────────────

function ResourcesAdmin({ onBack, desktop }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // row id or 'new'
  const [draft, setDraft] = useState({ title: '', description: '', url: '', session_tag: '' });

  const load = async () => {
    if (!hasSupabase) return;
    const { data } = await supabase
      .from('resources')
      .select('*')
      .order('sort_order', { ascending: true });
    setRows(data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing('new');
    setDraft({ title: '', description: '', url: '', session_tag: '', active: true });
  };

  const startEdit = (row) => {
    setEditing(row.id);
    setDraft({
      title: row.title || '',
      description: row.description || '',
      url: row.url || '',
      session_tag: row.session_tag || '',
      sort_order: row.sort_order ?? 100,
      active: row.active !== false,
    });
  };

  const save = async () => {
    try {
      const payload = { ...draft };
      if (editing !== 'new') payload.id = editing;
      const updated = await adminCall('resources.upsert', { row: payload });
      setRows((prev) => {
        const existing = prev.find((r) => r.id === updated.row.id);
        if (existing) return prev.map((r) => (r.id === updated.row.id ? updated.row : r));
        return [...prev, updated.row];
      });
      setEditing(null);
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (row) => {
    if (!confirm(`Delete "${row.title}"?`)) return;
    try {
      await adminCall('resources.delete', { id: row.id });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <AdminPaneShell title="Edit Resource Library" onBack={onBack} desktop={desktop}>
      <div
        style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.45)',
          padding: '0 2px 8px',
          lineHeight: 1.5,
        }}
      >
        Session prompts ship with the app. Use this screen to add or edit external links and downloads
        that appear on the Resources tab.
      </div>

      {editing && (
        <Card style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_DARK }}>
            {editing === 'new' ? 'New Resource' : 'Edit Resource'}
          </div>
          <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} placeholder="Resource title" />
          <Field label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} placeholder="One-line description" multiline />
          <Field label="URL" value={draft.url} onChange={(v) => setDraft({ ...draft, url: v })} placeholder="https://…" />
          <Field label="Session Tag" value={draft.session_tag} onChange={(v) => setDraft({ ...draft, session_tag: v })} placeholder="e.g. Session 2" />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={save} style={pillBtn('success')}>Save</button>
            <button onClick={() => setEditing(null)} style={pillBtn('muted')}>Cancel</button>
          </div>
        </Card>
      )}

      {!editing && (
        <button onClick={startNew} style={{ ...pillBtn('neutral'), padding: '10px 14px', alignSelf: 'flex-start' }}>
          + Add resource
        </button>
      )}

      <SectionLabel>STORED IN DATABASE · {rows.length}</SectionLabel>
      {rows.length === 0 && (
        <EmptyState text="No DB resources yet. Static prompts are shown by default." />
      )}
      {rows.map((row) => (
        <Card key={row.id} style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: ACCENT, letterSpacing: '0.07em', marginBottom: 2 }}>
            {row.session_tag || '—'}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: TEXT_DARK }}>{row.title}</div>
          {row.description && (
            <div style={{ fontSize: 12, color: TEXT_MID, marginTop: 4, lineHeight: 1.45 }}>
              {row.description}
            </div>
          )}
          {row.url && (
            <div
              style={{
                fontSize: 11.5,
                color: TEXT_LIGHT,
                marginTop: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.url}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button onClick={() => startEdit(row)} style={pillBtn('neutral')}>Edit</button>
            <button onClick={() => remove(row)} style={pillBtn('danger')}>Delete</button>
          </div>
        </Card>
      ))}
    </AdminPaneShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Commitments admin (read + delete)
// ────────────────────────────────────────────────────────────────────────────

function CommitmentsAdmin({ onBack, desktop }) {
  const [rows, setRows] = useState([]);

  const load = async () => {
    if (!hasSupabase) return;
    const { data } = await supabase
      .from('commitments')
      .select('*')
      .order('created_at', { ascending: false });
    setRows(data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (row) => {
    if (!confirm(`Delete commitment from ${row.name}?`)) return;
    try {
      await adminCall('commitments.delete', { id: row.id });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <AdminPaneShell title="All Commitments" onBack={onBack} desktop={desktop}>
      {rows.length === 0 && <EmptyState text="No commitments recorded yet." />}
      {rows.map((row) => (
        <Card key={row.id} style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_DARK }}>{row.name}</div>
            <div
              style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 20,
                padding: '2px 9px',
                fontSize: 10.5,
                fontWeight: 600,
                color: '#16a34a',
              }}
            >
              Due {row.by_when}
            </div>
          </div>
          <p style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.5, margin: '4px 0 8px' }}>
            {row.commitment}
          </p>
          <button onClick={() => remove(row)} style={pillBtn('danger')}>
            Delete
          </button>
        </Card>
      ))}
    </AdminPaneShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Attendees admin — CRUD + two independent per-row toggles:
//   share_contact = false  → name/title/org still show on AttendeesScreen,
//                            phone link + Download vCard button are hidden.
//   hidden        = true   → row stays in the DB but is removed from the
//                            front-end list entirely (also skipped by the
//                            offline vCard export script).
// ────────────────────────────────────────────────────────────────────────────

const EMPTY_ATTENDEE = {
  name: '',
  title: '',
  organization: '',
  state: '',
  phone: '',
  email: '',
  website: '',
  share_contact: true,
  hidden: false,
};

function AttendeesAdmin({ onBack, desktop }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row id or 'new'
  const [draft, setDraft] = useState(EMPTY_ATTENDEE);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!hasSupabase) {
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('attendees')
      .select('*')
      .order('name', { ascending: true });
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing('new');
    setDraft({ ...EMPTY_ATTENDEE });
  };

  const startEdit = (row) => {
    setEditing(row.id);
    setDraft({
      name: row.name || '',
      title: row.title || '',
      organization: row.organization || '',
      state: row.state || '',
      phone: row.phone || '',
      email: row.email || '',
      website: row.website || '',
      share_contact: row.share_contact !== false,
      hidden: row.hidden === true,
    });
  };

  const save = async () => {
    if (!draft.name.trim()) {
      alert('Name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...draft };
      if (editing !== 'new') payload.id = editing;
      const updated = await adminCall('attendees.upsert', { row: payload });
      setRows((prev) => {
        const existing = prev.find((r) => r.id === updated.row.id);
        const next = existing
          ? prev.map((r) => (r.id === updated.row.id ? updated.row : r))
          : [...prev, updated.row];
        return next.sort((a, b) => a.name.localeCompare(b.name));
      });
      setEditing(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Quick toggles that don't open the edit form — flip a single flag on a
  // row without re-typing anything. Both go through the same upsert path so
  // the server-side payload validation stays consistent.
  const patchRow = async (row, patch) => {
    try {
      const updated = await adminCall('attendees.upsert', {
        row: {
          id: row.id,
          name: row.name,
          title: row.title,
          organization: row.organization,
          state: row.state,
          phone: row.phone,
          email: row.email,
          website: row.website,
          role: row.role,
          share_contact: row.share_contact !== false,
          hidden: row.hidden === true,
          ...patch,
        },
      });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated.row : r)));
    } catch (e) {
      alert(e.message);
    }
  };
  const toggleShare = (row) => patchRow(row, { share_contact: !row.share_contact });
  const toggleHidden = (row) => patchRow(row, { hidden: !row.hidden });

  const remove = async (row) => {
    if (!confirm(`Delete ${row.name}? This cannot be undone.`)) return;
    try {
      await adminCall('attendees.delete', { id: row.id });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <AdminPaneShell title="Manage Attendees" onBack={onBack} desktop={desktop}>
      <div
        style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.45)',
          padding: '0 2px 8px',
          lineHeight: 1.5,
        }}
      >
        Changes appear on the Attendees screen instantly via realtime. Two
        independent toggles per attendee:
        {' '}<b style={{ color: 'rgba(255,255,255,0.7)' }}>Sharing / Private</b>
        {' '}hides only the phone + Download vCard button (name + org still
        show).
        {' '}<b style={{ color: 'rgba(255,255,255,0.7)' }}>Listed / Hidden</b>
        {' '}removes the attendee from the front-end list entirely — the row
        stays in the DB but no one sees it.
      </div>

      {editing && (
        <Card
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '14px 16px',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_DARK }}>
            {editing === 'new' ? 'New Attendee' : 'Edit Attendee'}
          </div>
          <Field
            label="Name"
            value={draft.name}
            onChange={(v) => setDraft({ ...draft, name: v })}
            placeholder="First Last"
          />
          <Field
            label="Title"
            value={draft.title}
            onChange={(v) => setDraft({ ...draft, title: v })}
            placeholder="Executive Director"
          />
          <Field
            label="Organization"
            value={draft.organization}
            onChange={(v) => setDraft({ ...draft, organization: v })}
            placeholder="State Chiropractic Association"
          />
          <Field
            label="State (2 letters)"
            value={draft.state}
            onChange={(v) => setDraft({ ...draft, state: v.toUpperCase().slice(0, 2) })}
            placeholder="IL"
          />
          <Field
            label="Phone"
            value={draft.phone}
            onChange={(v) => setDraft({ ...draft, phone: v })}
            placeholder="217-555-1234"
          />
          <Field
            label="Email"
            value={draft.email}
            onChange={(v) => setDraft({ ...draft, email: v })}
            placeholder="name@example.org"
          />
          <Field
            label="Website"
            value={draft.website}
            onChange={(v) => setDraft({ ...draft, website: v })}
            placeholder="example.org"
          />
          <LabeledToggle
            on={draft.share_contact}
            onChange={(v) => setDraft({ ...draft, share_contact: v })}
            title="Share contact details"
            onDescription="Phone and Download vCard button are visible to other attendees."
            offDescription="Phone and Download vCard button are hidden. Name + org still show."
          />
          <LabeledToggle
            on={!draft.hidden}
            onChange={(v) => setDraft({ ...draft, hidden: !v })}
            title="Listed on Attendees screen"
            onDescription="Attendee appears in the front-end roster."
            offDescription="Row stays in the DB but is hidden from every attendee — no name, no card, not counted."
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <button onClick={save} disabled={saving} style={pillBtn('success')}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => setEditing(null)} style={pillBtn('muted')}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      {!editing && (
        <button
          onClick={startNew}
          style={{ ...pillBtn('neutral'), padding: '10px 14px', alignSelf: 'flex-start' }}
        >
          + Add attendee
        </button>
      )}

      {loading && <SectionLabel>LOADING…</SectionLabel>}
      {!loading && rows.length === 0 && <EmptyState text="No attendees yet." />}
      {!loading && rows.length > 0 && (
        <SectionLabel>
          ROSTER · {rows.filter((r) => r.hidden !== true).length} VISIBLE
          {rows.some((r) => r.hidden === true)
            ? ` · ${rows.filter((r) => r.hidden === true).length} HIDDEN`
            : ''}
        </SectionLabel>
      )}
      {rows.map((row) => {
        const isHidden = row.hidden === true;
        return (
          <Card
            key={row.id}
            // Visually mute hidden rows so it's obvious at a glance which
            // attendees the front end isn't showing. Dashed border + reduced
            // opacity is enough — still legible, but clearly "off." Only set
            // `background` when hidden; the Card default fills it otherwise
            // (object spread treats `undefined` as an override, not a skip).
            style={{
              padding: '12px 14px',
              border: isHidden ? '1.5px dashed #cbd5e1' : '1.5px solid transparent',
              ...(isHidden && { opacity: 0.55, background: '#f8fafc' }),
            }}
          >
            {isHidden && (
              <div
                style={{
                  display: 'inline-block',
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  color: '#b45309',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 6,
                  padding: '1px 7px',
                  marginBottom: 6,
                }}
              >
                HIDDEN
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: TEXT_DARK }}>
                  {row.name}
                  {row.state && (
                    <span
                      style={{
                        fontSize: 10.5,
                        color: TEXT_LIGHT,
                        marginLeft: 8,
                        fontWeight: 500,
                      }}
                    >
                      {row.state}
                    </span>
                  )}
                </div>
                {row.title && (
                  <div style={{ fontSize: 12, color: TEXT_MID, marginTop: 1 }}>
                    {row.title}
                  </div>
                )}
                {row.organization && (
                  <div style={{ fontSize: 11.5, color: TEXT_LIGHT, marginTop: 2 }}>
                    {row.organization}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 11,
                    color: TEXT_LIGHT,
                    marginTop: 4,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                  }}
                >
                  {row.email && <span>{row.email}</span>}
                  {row.phone && <span>{row.phone}</span>}
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  alignItems: 'flex-end',
                  flexShrink: 0,
                }}
              >
                <StatusPill
                  on={row.share_contact !== false}
                  onLabel="Sharing"
                  offLabel="Private"
                  onClick={() => toggleShare(row)}
                  variant={row.share_contact === false ? 'warning' : undefined}
                />
                <StatusPill
                  on={!isHidden}
                  onLabel="Listed"
                  offLabel="Hidden"
                  onClick={() => toggleHidden(row)}
                  variant={isHidden ? 'warning' : undefined}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={() => startEdit(row)} style={pillBtn('neutral')}>
                Edit
              </button>
              <button onClick={() => remove(row)} style={pillBtn('danger')}>
                Delete
              </button>
            </div>
          </Card>
        );
      })}
    </AdminPaneShell>
  );
}

// Labeled switch used in the attendee edit form. Visual matches the Field
// component so it slots into the form layout cleanly. Used for the
// share_contact toggle and the listed/hidden toggle.
function LabeledToggle({ on, onChange, title, onDescription, offDescription }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        border: '1.5px solid #e2e8f0',
        borderRadius: 8,
        background: '#fff',
        cursor: 'pointer',
        fontFamily: FONT,
        textAlign: 'left',
      }}
    >
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: TEXT_DARK }}>
          {title}
        </div>
        <div style={{ fontSize: 11.5, color: TEXT_LIGHT, marginTop: 2 }}>
          {on ? onDescription : offDescription}
        </div>
      </div>
      <Ic.Toggle size={22} on={on} color={ACCENT} />
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Bits
// ────────────────────────────────────────────────────────────────────────────

function StatusPill({ on, onLabel, offLabel, onClick, variant }) {
  const colors = on
    ? variant === 'warning'
      ? { bg: '#fef3c7', border: '#fde68a', text: '#b45309' }
      : { bg: ACCENT_LIGHT, border: ACCENT_BORDER, text: ACCENT }
    : { bg: '#f8fafc', border: '#e2e8f0', text: TEXT_MID };
  return (
    <button
      onClick={onClick}
      style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        borderRadius: 7,
        padding: '4px 9px',
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: FONT,
        letterSpacing: '0.04em',
      }}
    >
      {on ? onLabel : offLabel}
    </button>
  );
}

function pillBtn(variant) {
  const map = {
    success: { bg: '#dcfce7', border: '#86efac', color: '#16a34a' },
    danger: { bg: '#fee2e2', border: '#fca5a5', color: '#b91c1c' },
    muted: { bg: '#f8fafc', border: '#e2e8f0', color: TEXT_MID },
    neutral: { bg: ACCENT_LIGHT, border: ACCENT_BORDER, color: ACCENT },
  };
  const c = map[variant] || map.muted;
  return {
    background: c.bg,
    border: `1px solid ${c.border}`,
    color: c.color,
    borderRadius: 7,
    padding: '6px 11px',
    fontSize: 11.5,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: FONT,
  };
}

function EmptyState({ text }) {
  return (
    <div
      style={{
        fontSize: 12.5,
        color: 'rgba(255,255,255,0.45)',
        padding: '14px 4px',
        textAlign: 'center',
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
